import { useEffect } from 'react';
import { Check } from 'lucide-react';
import { extractMoneyDigits } from '../utils/moneyInput';

const keypadLayout = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['00', '0', 'backspace'],
  [{ key: 'clear', colSpan: 3 }]
];

function MobileAmountKeypad({ isOpen, value = '', onChange, onClose }) {
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const sanitizedValue = extractMoneyDigits(value);

  const handleDigit = (digit) => {
    const nextDigits = extractMoneyDigits(`${sanitizedValue}${digit}`);
    onChange?.(nextDigits);
  };

  const handleBackspace = () => {
    onChange?.(sanitizedValue.slice(0, -1));
  };

  const handleClear = () => {
    onChange?.('');
  };

  const renderLabel = (key) => {
    if (key === 'backspace') return '⌫';
    if (key === 'clear') return 'Canc';
    return key;
  };

  const getButtonHandler = (key) => {
    if (key === 'backspace') return handleBackspace;
    if (key === 'clear') return handleClear;
    return () => handleDigit(key);
  };

  const getButtonProps = (key) => {
    const baseClasses = 'h-12 rounded-xl text-base font-semibold shadow-sm transition active:scale-95';

    if (key === 'backspace' || key === 'clear') {
      return {
        className: `${baseClasses} bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/40 dark:text-blue-100 dark:hover:bg-blue-900/60`,
        'aria-label': key === 'backspace' ? 'Cancella ultimo carattere' : 'Azzera importo'
      };
    }

    return {
      className: `${baseClasses} bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700`
    };
  };

  return (
    <div
      className="fixed inset-0 z-[120000] flex items-end justify-center bg-transparent p-4 pb-8"
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-xs rounded-2xl bg-white shadow-2xl dark:bg-gray-900 max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-800">
          <button
            type="button"
            onClick={handleClear}
            className="flex h-10 min-w-[72px] items-center justify-center rounded-lg bg-red-500 text-white shadow-sm transition hover:bg-red-600 active:scale-95"
            aria-label="Cancella importo"
          >
            C
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 min-w-[72px] items-center justify-center rounded-lg bg-green-500 text-white shadow-sm transition hover:bg-green-600 active:scale-95"
            aria-label="Chiudi tastierino"
          >
            <Check className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 px-4 pt-2 pb-4">
          {keypadLayout.flat().map((keyData) => {
            const key = typeof keyData === 'string' ? keyData : keyData.key;
            if (key === 'clear') {
              return null;
            }
            const buttonProps = getButtonProps(key);
            const ariaLabel = buttonProps['aria-label'];
            return (
              <button
                key={key}
                type="button"
                onClick={getButtonHandler(key)}
                className={buttonProps.className}
                aria-label={ariaLabel || undefined}
              >
                {renderLabel(key)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default MobileAmountKeypad;
